import React, { useState, useEffect, useRef } from 'react';
import {
    TextField, Button, Box, List, ListItem, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableRow, Avatar, Dialog, DialogTitle, DialogContent,
    DialogActions, DialogContentText
} from '@mui/material';
import imagenLogo from '../../../img/injupemplogo.png';
import SendIcon from '@mui/icons-material/Send';
import { ContextSession } from "../../../services/ContextSession";
import { useContext } from "react";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import nuevoMensaje from './nuevo mensaje.mp3';

function VerConsultaChat() {
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [isClosed, setIsClosed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    let sessi = useContext(ContextSession);
    const messagesEndRef = useRef(null);
    const audioRef = useRef(new Audio(nuevoMensaje));

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);


    const fetchMessages = async () => {
        try {
            const ticketId = sessi.getVariable("SolicitudChatbot");
            const response = await fetch(`https://chatbot.injupemp.gob.hn/obtener_historial_mensajes/${ticketId}/`);
            const data = await response.json();
            if (data.historial) {
                const formattedMessages = data.historial.map((item) => ({
                    user: item.remitente === 'Agente' ? 'INJUPEMP' : selectedTicket?.nombre.toUpperCase() || 'Usuario desconocido',
                    text: item.mensaje,
                    timestamp: item.fecha_hora !== 'Desconocido' ? item.fecha_hora : 'Fecha desconocida',
                }));

                setMessages((prevMessages) => {
                    if (JSON.stringify(prevMessages) !== JSON.stringify(formattedMessages)) {
                        // Detectar si el nuevo mensaje es de un usuario (no enviado por INJUPEMP)
                        if (formattedMessages.length > prevMessages.length) {
                            const lastMessage = formattedMessages[formattedMessages.length - 1];
                            if (lastMessage.user !== 'INJUPEMP') {
                                audioRef.current.play(); // Reproducir sonido
                            }
                        }
                        return formattedMessages;
                    }
                    return prevMessages;
                });
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    useEffect(() => {
        const fetchTicketData = async () => {
            try {
                const ticketId = parseInt(sessi.getVariable("SolicitudChatbot"));
                const response = await fetch(`https://chatbot.injupemp.gob.hn/obtener_ticket_por_id/?id=${ticketId}`); const data = await response.json();
                const ticket = data.tickets.find(t => t.id === ticketId);
                if (ticket) {
                    setSelectedTicket(ticket);
                    if (ticket.estado.toLowerCase() === 'cerrado') {
                        setIsClosed(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching ticket data:', error);
            }
        };

        fetchTicketData();
    }, [sessi]);

    useEffect(() => {
        if (selectedTicket) {
            fetchMessages(); // Carga los mensajes al menos una vez
        }

        if (!isClosed) { // Configura el intervalo solo si no está cerrado
            const interval = setInterval(fetchMessages, 5000);
            return () => clearInterval(interval);
        }
    }, [selectedTicket, isClosed]); // Dependencias incluyen selectedTicket e isClosed


    const sendMessage = async () => {
        if (message.trim() === '') {
            alert('El campo de mensaje no puede estar vacío.');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch('https://chatbot.injupemp.gob.hn/responder_ticket/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    agente_id: sessi.sessionData.id,
                    ticket_id: sessi.getVariable("SolicitudChatbot"),
                    mensaje: message,
                }),
            });

            if (response.ok) {
                setMessages([...messages, { user: 'INJUPEMP', text: message, timestamp: new Date().toLocaleString() }]);
                setMessage('');
            } else {
                alert('Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Ocurrió un error al enviar el mensaje.');
        } finally {
            setIsLoading(false);
        }
    };

    const closeRequest = async () => {
        try {
            const ticketId = sessi.getVariable("SolicitudChatbot");
            await fetch(`https://chatbot.injupemp.gob.hn/cerrar_ticket/${ticketId}/`, {
                method: 'POST',
            });
            setDialogOpen(true); // Muestra un mensaje indicando que se cerró la solicitud
        } catch (error) {
            console.error('Error closing ticket:', error);
        }
    };

    const handleConfirmClose = () => {
        setConfirmDialogOpen(false); // Cierra el diálogo de confirmación
        closeRequest(); // Llama a la función para cerrar la solicitud
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setIsClosed(true);
    };

    const renderMessages = () => {
        return messages.map((msg, index) => (
            <ListItem key={index} sx={{ display: 'flex', justifyContent: msg.user === 'INJUPEMP' ? 'flex-end' : 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {msg.user !== 'INJUPEMP' && (
                        <Avatar sx={{ backgroundColor: 'grey', marginRight: 1 }} />
                    )}
                    {msg.user === 'INJUPEMP' && (
                        <img src={imagenLogo} alt="Call Center" style={{ width: '64px', height: '34px', marginRight: 5 }} />
                    )}
                    <Paper
                        sx={{
                            padding: '10px',
                            backgroundColor: msg.user !== 'INJUPEMP' ? '#c8e6c9' : '#e0f7fa',
                            marginBottom: '10px',
                            borderRadius: '8px',
                            boxShadow: 2,
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            overflowWrap: 'break-word',
                            wordBreak: 'break-word',
                            display: 'inline-block',
                        }}
                    >
                        <Typography variant="caption" style={{ fontSize: 14, fontWeight: 'bold' }}>
                            {msg.user}:
                        </Typography>
                        {/*<Typography variant="body2" style={{ fontSize: 17 }}>{msg.text}</Typography>*/}
                        <Typography variant="body2" style={{ fontSize: 17 }} component="div">
                            {msg.text.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    <br />
                                </React.Fragment>
                            ))}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 14 }}>{msg.timestamp}</Typography>
                    </Paper>
                </Box>
            </ListItem>
        ));
    };

    return (
        <>
            <Typography variant="h4" fontWeight="bold" align="center" gutterBottom marginTop={5}>
                Solicitud #{sessi.getVariable("SolicitudChatbot")} - INJUPEMP Virtual
            </Typography>

            <Box sx={{ padding: 3, maxWidth: 900, margin: 'auto', backgroundColor: '#f5f5f5', borderRadius: '12px', boxShadow: 3, marginTop: 3 }}>
                <Typography variant="h5" gutterBottom align="center"><b>Datos De La Persona:</b></Typography>

                <TableContainer sx={{ marginBottom: 2 }} component={Paper}>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell><Typography variant="subtitle1" fontWeight="bold">Nombre:</Typography></TableCell>
                                <TableCell>{selectedTicket?.nombre?.toUpperCase() || 'Cargando...'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><Typography variant="subtitle1" fontWeight="bold">Identidad:</Typography></TableCell>
                                <TableCell>{selectedTicket?.dni || 'Cargando...'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><Typography variant="subtitle1" fontWeight="bold">Teléfono:</Typography></TableCell>
                                <TableCell>{selectedTicket?.telefono || 'Cargando...'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><Typography variant="subtitle1" fontWeight="bold">Teléfono Alterno:</Typography></TableCell>
                                <TableCell>{selectedTicket?.telefono_alterno ?? 'NA'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><Typography variant="subtitle1" fontWeight="bold">Estado de la Solicitud:</Typography></TableCell>
                                <TableCell>{selectedTicket?.estado.toUpperCase() || 'Cargando...'}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography variant="h5" gutterBottom align="center"><b>Conversación:</b></Typography>

                <List sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {renderMessages()}
                    <div ref={messagesEndRef} />
                </List>


                {!isClosed && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginTop: 2 }}>
                        <TextField
                            label="Escribe un mensaje"
                            fullWidth
                            multiline
                            variant="outlined"
                            value={message}
                            onChange={(e) => {
                                const input = e.target.value;
                                // Capitaliza solo la primera letra sin afectar el resto del texto
                                const formattedInput = input.charAt(0).toUpperCase() + input.slice(1);
                                setMessage(formattedInput);
                            }}
                            disabled={isClosed || isLoading}
                            sx={{
                                borderRadius: '20px',
                                '& .MuiOutlinedInput-root': { borderRadius: '20px' },
                            }}
                            inputProps={{
                                spellCheck: true, // Activar corrección ortográfica
                                lang: 'es', // Configuración del idioma en español
                            }}
                        />



                        <Button
                            variant="contained"
                            color="success"
                            onClick={sendMessage}
                            disabled={isClosed || isLoading}
                            sx={{ borderRadius: '18px' }}
                            endIcon={<SendIcon />}
                        >
                            {isLoading ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </Box>
                )}

                {!isClosed && (
                    <Box sx={{ marginTop: 2 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={() => setConfirmDialogOpen(true)} // Abre el diálogo de confirmación
                            sx={{
                                borderRadius: '10px',
                                '&:hover': { backgroundColor: '#d32f2f', color: 'white' }
                            }}
                        >
                            Finalizar Solicitud
                        </Button>
                    </Box>
                )}

                {isClosed && (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography variant="body1" color="textSecondary" align="center">
                            La solicitud está cerrada.
                        </Typography>
                    </Box>
                )}
            </Box>

            {!isClosed && (
                <Box
                    sx={{
                        maxWidth: 900,
                        margin: 'auto',
                        marginTop: 1,
                    }}
                >
                    {/* Contenido existente del Box */}

                    {/* Botones centrados */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 3 }}>
                        {/* Botón azul */}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                setMessage(
                                    `Hola! gracias por contactar con INJUPEMP, le saluda ${sessi.sessionData.nombre.split(' ')[0]} ${sessi.sessionData.apellido.split(' ')[0]}. Le ayudaré con sus consultas el día de hoy. *Le recuerdo que la opción de notas de voz, así como el envío de archivos multimedia no está disponible a través de este canal de atención.*`
                                )
                            }
                            sx={{
                                borderRadius: '10px',
                                padding: '10px 20px',
                            }}
                        >
                            Mensaje Inicial
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                setMessage(
                                    `¡Realiza tu Prueba de Sobrevivencia de forma fácil, rápida y segura desde nuestro portal web, sin necesidad de desplazarte a las oficinas de INJUPEMP!\n` +
                                    `Ahora puedes completar tu prueba en línea, desde cualquier lugar y en pocos minutos. Solo necesitas tu número de identidad, un teléfono móvil y acceso a internet. Al finalizar el proceso, recibirás automáticamente tu comprobante por WhatsApp.\n\n` +
                                    `Sigue estos sencillos pasos para completar el proceso:\n` +
                                    `1. Ingresa a https://sobrevivencia.injupemp.gob.hn/\n` +
                                    `2. Pulsa sobre el botón “Pulsa Aquí Para Iniciar”\n` +
                                    `3. Acepta los términos y condiciones y la política de privacidad.\n` +
                                    `4. Pulsa el botón “Iniciar”\n` +
                                    `5. Ingresa tu número de identidad y número de teléfono móvil\n` +
                                    `(Recuerda: El comprobante se enviará automáticamente por WhatsApp al número ingresado. Asegúrate de proporcionar un número válido y personal.)\n` +
                                    `6. Pulsa el botón “Verificar Identidad”\n` +
                                    `7. Acepta los permisos para el uso de cámara del navegador.\n` +
                                    `8. Coloca tu rostro sobre el óvalo marcado en pantalla.\n` +
                                    `9. Espera que finalice el reconocimiento facial.\n` +
                                    `10. Una vez completado el reconocimiento, llena el formulario final y pulsa el botón “Enviar”.\n\n` +
                                    `¡Listo! Recibirás el comprobante al número de WhatsApp indicado.\n\n` +
                                    `¡Hazlo en línea, sin filas ni traslados, y cumple con tu prueba de sobrevivencia en minutos!`
                                )
                            }
                            sx={{
                                borderRadius: '10px',
                                padding: '10px 20px',
                            }}
                        >
                            Prueba Sobrevivencia
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                setMessage(
                                    `Realiza tus trámites de forma 100% en línea desde cualquier lugar, ya sea tu hogar o lugar de trabajo, sin necesidad de trasladarte a nuestras oficinas desde nuestra nueva plataforma digital *INJUPEMP Conectados*.\n\n` +
                                    `Los servicios disponibles en nuestra plataforma son:\n` +
                                    `- Solicitud de preanálisis de préstamos personales e hipotecarios.\n` +
                                    `- Descarga de constancia de pensionado.\n` +
                                    `- Visualiza tu saldo cotizado, promedio de salarios cotizados, pensión aproximada y tus últimos salarios cotizados.\n` +
                                    `- Envío de quejas y sugerencias.\n\n` +
                                    `Muy pronto muchos más servicios disponibles.\n` +
                                    `*Ingresa ya a la dirección web:* conectados.injupemp.gob.hn`
                                )
                            }
                            sx={{
                                borderRadius: '10px',
                                padding: '10px 20px',
                            }}
                        >
                            INJUPEMP Conectados
                        </Button>

                        {/* Botón naranja */}
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: 'orange',
                                '&:hover': { backgroundColor: 'darkorange' },
                                borderRadius: '10px',
                                padding: '10px 20px',
                            }}
                            onClick={() =>
                                setMessage("¿Tiene alguna consulta adicional en la que le pueda apoyar este día?")
                            }
                        >
                            Consulta Adicional
                        </Button>

                        {/* Botón naranja */}
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: 'orange',
                                '&:hover': { backgroundColor: 'darkorange' },
                                borderRadius: '10px',
                                padding: '10px 20px',
                            }}
                            onClick={() =>
                                setMessage("¿Estimado usuario aún se encuentra en línea?")
                            }
                        >
                            ¿Sigue disponible?
                        </Button>

                        {/* Botón naranja */}
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: 'orange',
                                '&:hover': { backgroundColor: 'darkorange' },
                                borderRadius: '10px',
                                padding: '10px 20px',
                            }}
                            onClick={() =>
                                setMessage("Ha sido un placer ayudarte. Si tienes más preguntas en el futuro, no dudes en contactarnos nuevamente. ¡Te deseamos un excelente día!")
                            }
                        >
                            Ha sido un placer ayudarte...
                        </Button>
                    </Box>
                </Box>
            )}

            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleIcon style={{ color: 'green', marginRight: '8px', verticalAlign: 'middle' }} />
                    Solicitud Cerrada
                </DialogTitle>
                <DialogContent>
                    <Typography>La solicitud fue cerrada con éxito.</Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleDialogClose} color="primary" variant="contained">
                        Aceptar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialogo de confirmación */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title" sx={{ textAlign: 'center' }}>
                    Confirmar
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        ¿Está seguro de que desea cerrar la solicitud?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        color="error"
                        variant="contained"
                        endIcon={<Cancel />}
                    >
                        No
                    </Button>
                    <Button
                        onClick={handleConfirmClose}
                        color="success"
                        variant="contained"
                        endIcon={<CheckCircleIcon />}
                        autoFocus
                    >
                        Sí
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default VerConsultaChat;


/*
import React, { useState, useEffect } from 'react';
import {
    TextField, Button, Box, List, ListItem, Typography, Paper, Table, TableBody,
    TableCell, TableContainer, TableRow, Avatar, Dialog, DialogTitle, DialogContent,
    DialogActions, DialogContentText
} from '@mui/material';
import imagenLogo from '../../../img/injupemplogo.png';
import SendIcon from '@mui/icons-material/Send';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { ContextSession } from "../../../services/ContextSession";
import { useContext } from "react";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close'
import { Cancel } from '@mui/icons-material';

function VerConsultaChat() {
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [isClosed, setIsClosed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    let sessi = useContext(ContextSession);

    useEffect(() => {
        const fetchTicketData = async () => {
            try {
                const response = await fetch('https://chatbot.injupemp.gob.hn/obtener_tickets/');
                const data = await response.json();
                const ticketId = parseInt(sessi.getVariable("SolicitudChatbot"));
                const ticket = data.tickets.find(t => t.id === ticketId);
                if (ticket) {
                    setSelectedTicket(ticket);
                    setMessages([{ user: 'Cliente', text: ticket.mensaje, timestamp: formatDate(ticket.fecha_inicio) }]);
                    if (ticket.estado.toLowerCase() === 'cerrado') {
                        setIsClosed(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching ticket data:', error);
            }
        };

        fetchTicketData();
    }, [sessi]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true };
        return new Date(dateString).toLocaleString('es-HN', options);
    };

    const sendMessage = async () => {
        if (message.trim() === '') {
            alert('El campo de mensaje no puede estar vacío.');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch('https://chatbot.injupemp.gob.hn/responder_ticket/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    agente_id: sessi.sessionData.id,
                    ticket_id: sessi.getVariable("SolicitudChatbot"),
                    mensaje: message,
                }),
            });

            if (response.ok) {
                setMessages([...messages, { user: 'Call Center', text: message, timestamp: new Date().toLocaleString() }]);
                setMessage('');
            } else {
                alert('Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Ocurrió un error al enviar el mensaje.');
        } finally {
            setIsLoading(false);
        }
    };

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const closeRequest = async () => {
        try {
            const ticketId = sessi.getVariable("SolicitudChatbot");
            await fetch(`https://chatbot.injupemp.gob.hn/cerrar_ticket/${ticketId}/`, {
                method: 'POST',
            });
            setDialogOpen(true); // Muestra un mensaje indicando que se cerró la solicitud
        } catch (error) {
            console.error('Error closing ticket:', error);
        }
    };

    const handleConfirmClose = () => {
        setConfirmDialogOpen(false); // Cierra el diálogo de confirmación
        closeRequest(); // Llama a la función para cerrar la solicitud
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setIsClosed(true);
        const fetchTicketData = async () => {
            try {
                const response = await fetch('https://chatbot.injupemp.gob.hn/obtener_tickets/');
                const data = await response.json();
                const ticketId = parseInt(sessi.getVariable("SolicitudChatbot"));
                const ticket = data.tickets.find(t => t.id === ticketId);
                if (ticket) {
                    setSelectedTicket(ticket);
                }
            } catch (error) {
                console.error('Error fetching ticket data:', error);
            }
        };

        fetchTicketData();
    };

    const renderMessages = () => {
        return messages.map((msg, index) => (
            <ListItem key={index} sx={{ display: 'flex', justifyContent: msg.user === 'Cliente' ? 'flex-start' : 'flex-end' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {msg.user === 'Cliente' && (
                        <Avatar sx={{ backgroundColor: 'grey', marginRight: 1 }} />
                    )}
                    {msg.user === 'Call Center' && (
                        <img src={imagenLogo} alt="Call Center" style={{ width: '64px', height: '34px', marginRight: 5 }} />
                    )}
                    <Paper
                        sx={{
                            padding: '10px',
                            backgroundColor: msg.user === 'Cliente' ? '#c8e6c9' : '#e0f7fa',
                            marginBottom: '10px',
                            borderRadius: '8px',
                            boxShadow: 2,
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            overflowWrap: 'break-word',
                            wordBreak: 'break-word',
                            display: 'inline-block',
                        }}
                    >
                        <Typography variant="caption" style={{ fontSize: 14, fontWeight: 'bold' }}>
                            {msg.user === 'Cliente' ? selectedTicket?.nombre : 'INJUPEMP'}:
                        </Typography>
                        <Typography variant="body2" style={{ fontSize: 17 }}>{msg.text}</Typography>
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 14 }}>{msg.timestamp}</Typography>
                    </Paper>
                </Box>
            </ListItem>
        ));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 15 }}>
                <Typography variant="h4" fontWeight="bold">Solicitud #{sessi.getVariable("SolicitudChatbot")} - INJUPEMP Virtual</Typography>
            </div>

            <Box sx={{ padding: 3, maxWidth: 900, margin: 'auto', backgroundColor: '#f5f5f5', borderRadius: '12px', boxShadow: 3, marginTop: 3 }}>
                <Typography variant="h5" gutterBottom align="center"><b>Datos De La Persona:</b></Typography>

                <TableContainer sx={{ marginBottom: 2 }} component={Paper}>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell style={{ fontSize: 17, backgroundColor: '#ededed' }}><Typography variant="subtitle1" fontWeight="bold">Nombre:</Typography></TableCell>
                                <TableCell style={{ fontSize: 17, backgroundColor: '#ededed' }}>{selectedTicket?.nombre || 'Cargando...'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{ fontSize: 17 }}><Typography variant="subtitle1" fontWeight="bold">Identidad:</Typography></TableCell>
                                <TableCell style={{ fontSize: 17 }}>{selectedTicket?.dni || 'Cargando...'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{ fontSize: 17, backgroundColor: '#ededed' }}><Typography variant="subtitle1" fontWeight="bold">Teléfono:</Typography></TableCell>
                                <TableCell style={{ fontSize: 17, backgroundColor: '#ededed' }}>{selectedTicket?.telefono || 'Cargando...'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{ fontSize: 17 }}><Typography variant="subtitle1" fontWeight="bold">Estado de la Solicitud:</Typography></TableCell>
                                <TableCell style={{ fontSize: 17 }}>{selectedTicket?.estado || 'Cargando...'}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography variant="h5" gutterBottom align="center"><b>Conversación:</b></Typography>

                <List sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {renderMessages()}
                </List>

                {!isClosed && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginTop: 2 }}>
                        <TextField
                            label="Escribe un mensaje"
                            fullWidth
                            multiline
                            variant="outlined"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={isClosed || isLoading}
                            sx={{ borderRadius: '20px', '& .MuiOutlinedInput-root': { borderRadius: '20px' } }}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            variant="contained"
                            color="success"
                            onClick={sendMessage}
                            disabled={isClosed || isLoading}
                            sx={{ borderRadius: '18px' }}
                            endIcon={<SendIcon />}
                        >
                            {isLoading ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </Box>
                )}

 
                {!isClosed && (
                    <Box sx={{ marginTop: 2 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={() => setConfirmDialogOpen(true)} // Abre el diálogo de confirmación
                            sx={{
                                borderRadius: '10px',
                                '&:hover': { backgroundColor: '#d32f2f', color: 'white' }
                            }}
                        >
                            Finalizar Solicitud
                        </Button>
                    </Box>
                )}

                {isClosed && (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography variant="body1" color="textSecondary" align="center">
                            La solicitud está cerrada.
                        </Typography>
                    </Box>
                )}
            </Box>

            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleIcon style={{ color: 'green', marginRight: '8px', verticalAlign: 'middle' }} />
                    Solicitud Cerrada
                </DialogTitle>
                <DialogContent>
                    <Typography>La solicitud fue cerrada con éxito.</Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleDialogClose} color="primary" variant="contained">
                        Aceptar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title" sx={{ textAlign: 'center' }}>
                    Confirmar
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        ¿Está seguro de que desea cerrar la solicitud?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        color="error"
                        variant="contained"
                        endIcon={<Cancel />}
                    >
                        No
                    </Button>
                    <Button
                        onClick={handleConfirmClose}
                        color="success"
                        variant="contained"
                        endIcon={<CheckCircleIcon />}
                        autoFocus
                    >
                        Sí
                    </Button>
                </DialogActions>
            </Dialog>


        </>
    );
}

export default VerConsultaChat;

*/